import { Capacitor } from '@capacitor/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'

import { Card } from '@/@bothrs-ui/web/components/card/Card'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { P } from '@/@bothrs-ui/web/components/typography/Body'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { ContentPagePaddingCSS } from '@/layout/components/content-page-layout/ContentPageLayout.styled'
import { useHideNativeNavigation, useHideWebHeader } from '@/layout/providers/LayoutProvider'
import { mixpanel } from '@/mixpanel'
import { useOrders } from '@/modules/orders/hooks/useOrders'
import { PaymentStatus } from '@/modules/payment/components/PaymentStatus'
import { usePayment } from '@/modules/payment/hooks/usePayment'
import { TransactionStatus } from '@/modules/payment/types/payment.types'
import { mapPaymentStatusToTransactionStatus, mapPriceToCurrency } from '@/modules/payment/utils/payment.helpers'
import { getPomTransactionStatus } from '@/modules/payment/utils/pom.helpers'

import type { OrderResponse } from '@/modules/orders/types/order.type'

/**
 * This page is used to display the payment status of a care program payment
 *
 * - Transaction status = status of the transaction (POM redirect) - can be completed, processing or refused
 * - Payment status = status of the payment linked to the order - can be not_found, revoked, unpaid, completed, refunded, partially_refunded, marked, processing, scheduled
 * -> There is a difference between them because POM has no knowlegde of refused payment
 *
 * The page will poll the payment status every 1 second and update the transaction status accordingly
 * @returns
 */
export const CareProgramPaymentStatus = () => {
  // Hooks
  // ------------------------------------------------------------------------- /
  const { paymentId } = useParams<{ paymentId: string }>()
  const [searchParameters] = useSearchParams()
  const { getOrderById } = useOrders()
  const { checkPaymentStatus, cancelPayment } = usePayment()
  const navigate = useNavigate()

  // Hide navigation on native devices
  useHideWebHeader()
  useHideNativeNavigation()

  // Template variables
  // ------------------------------------------------------------------------- /

  const responseCode = searchParameters.get('responseCode')
  const documentId = paymentId ?? searchParameters.get('documentId')

  const transactionStatusFromPom = useMemo(
    () => (responseCode ? getPomTransactionStatus(responseCode) : { status: TransactionStatus.PROCESSING }),
    [responseCode],
  )

  // State
  // ------------------------------------------------------------------------- /
  const [transactionStatus, setTransactionStatus] = useState<TransactionStatus>(
    transactionStatusFromPom?.status ?? TransactionStatus.PROCESSING,
  )

  const [cancelButtonVisible, setCancelButtonVisible] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [order, setOrder] = useState<OrderResponse | null>(null)

  // Effects
  // ------------------------------------------------------------------------- /

  // Polling the payment status every 1 seconds if the transaction status is processing
  useEffect(() => {
    if (!documentId) return

    const startTime = Date.now()

    // If the transaction status is refused by POM, we set the transaction status to refused
    if (transactionStatusFromPom?.status === TransactionStatus.REFUSED) {
      setTransactionStatus(TransactionStatus.REFUSED)
      mixpanel.track('care_program_payment_failed', {
        responseCode,
        documentId,
        platform: Capacitor.getPlatform(),
        processingTime: Date.now() - startTime,
      })
      return
    }

    // If the transaction status is completed by POM, we set the transaction status to completed
    if (transactionStatusFromPom?.status === TransactionStatus.COMPLETED) {
      setTransactionStatus(TransactionStatus.COMPLETED)
      mixpanel.track('care_program_payment_success', {
        responseCode,
        documentId,
        platform: Capacitor.getPlatform(),
        processingTime: Date.now() - startTime,
      })
      return
    }

    // If the transaction status is something else or processing, we poll the payment status every 1 second

    const interval = setInterval(() => {
      checkPaymentStatus(documentId)
        .then((response) => {
          const status = response.data.status

          // If the order is not set, we fetch it from the API
          if (!order && response.data.orderId) {
            getOrderById(response.data.orderId).then((response) => {
              setOrder(response.data)
            })
          }

          // Map the order payment status to the transaction status
          const transactionStatus = mapPaymentStatusToTransactionStatus(status)
          setTransactionStatus(transactionStatus)
          if (transactionStatus !== TransactionStatus.PROCESSING) {
            clearInterval(interval)
          }
        })
        .catch((error) => {
          // If the payment status is not found, we set the transaction status to refused
          console.error(error)
          // A dropped connection is not a payment outcome: keep polling so the
          // real status shows once the device is back online.
          if (!navigator.onLine) return
          setHasError(true)
          clearInterval(interval)
        })
    }, 1000)

    return () => clearInterval(interval)
  }, [documentId, checkPaymentStatus, getOrderById, transactionStatusFromPom, responseCode, order])

  // Show the cancel button after 3 seconds if the transaction status is processing
  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null
    if (transactionStatus === TransactionStatus.PROCESSING) {
      timeout = setTimeout(() => {
        setCancelButtonVisible(true)
      }, 3000)
    } else {
      if (timeout) {
        clearTimeout(timeout)
      }
      setCancelButtonVisible(false)
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [transactionStatus])

  // Interaction methods
  // ------------------------------------------------------------------------- /
  const handleCta = async () => {
    if (transactionStatus === TransactionStatus.REFUSED) {
      mixpanel.track('care_program_payment_retry', {
        status: transactionStatus,
        documentId,
        platform: Capacitor.getPlatform(),
      })
      await handleClose()
    } else {
      mixpanel.track('care_program_payment_close_after_success', {
        status: transactionStatus,
        documentId,
        platform: Capacitor.getPlatform(),
      })
      navigate('/care')
    }
  }

  const handleCancel = async () => {
    mixpanel.track('care_program_payment_cancel', {
      status: transactionStatus,
      documentId,
      platform: Capacitor.getPlatform(),
    })

    if (documentId) {
      try {
        await cancelPayment(documentId)
      } catch (error) {
        console.error(error)
        setHasError(true)
      }
    }
    await handleClose()
  }

  /**
   * This function is used to close the payment status page
   * It will navigate to the care program detail page if the orderId is set
   * If the orderId is not set, it will try to fetch the orderId from the paymentId
   * If the orderId is not set, it will navigate to the care home page
   *
   * @returns {Promise<void>}
   */
  const handleClose = async () => {
    mixpanel.track('care_program_payment_close', {
      status: transactionStatus,
      documentId,
      platform: Capacitor.getPlatform(),
    })

    if (transactionStatus === TransactionStatus.COMPLETED) {
      navigate('/care')
      return
    }

    // On web, we navigate to the care program detail page (orderId will be set by the checkPaymentStatus hook)
    try {
      if (order) {
        const careProgramId = order.orderLines?.[0]?.externalId
        navigate(`/care/care-program/${careProgramId}`)
      } else if (documentId) {
        // Try to fetch the orderId from the paymentId
        const paymentStatus = await checkPaymentStatus(documentId)
        const orderResponse = await getOrderById(paymentStatus.data.orderId)
        setOrder(orderResponse.data)
        const careProgramId = orderResponse.data.orderLines?.[0]?.externalId
        navigate(`/care/care-program/${careProgramId}`)
      } else {
        // If the orderId is not set, we navigate to the care home page
        navigate('/care')
      }
    } catch (error) {
      console.error(error)
      navigate('/care')
    }
  }

  // Render
  // ------------------------------------------------------------------------- /

  return (
    <ContentPageLayout
      noPadding
      noScroll
      header={
        <StAppHeader
          secondaryAction={{
            icon: ['far', 'close'],
            iconSize: 'xl',
            label: t({ id: 'globals.close', message: 'Sluiten' }),
            onClick: handleClose,
          }}
        />
      }
    >
      <StContainer>
        <StContent>
          {hasError ? (
            <StErrorContainer>
              <H3 textAlign='center'>
                {t({
                  id: 'careProgramPaymentStatus.error.title',
                  message: 'Technische storing',
                })}
              </H3>
              <P type='BodyMediumRegular' textAlign='center'>
                {t({
                  id: 'careProgramPaymentStatus.error.body',
                  message: 'We ondervinden technische problemen, probeer het later opnieuw.',
                })}
              </P>
            </StErrorContainer>
          ) : (
            <PaymentStatus
              status={transactionStatus}
              successConfig={{
                title: t({
                  id: 'careProgramPaymentStatus.paymentSucceeded.title',
                  message: 'Bedankt!',
                }),
                body: (
                  <StStatusContainer>
                    {order?.totalAmount
                      ? t({
                          id: 'careProgramPaymentStatus.paymentSucceeded.body',
                          message:
                            'Bedankt! Je betaling van €{price} is succesvol verwerkt. Je ontvangt binnenkort een email.',
                          values: {
                            price: mapPriceToCurrency(order.totalAmount),
                          },
                        })
                      : t({
                          id: 'paymentStatus.success.body',
                          message: 'Bedankt! Je betaling is succesvol verwerkt. Je ontvangt binnenkort een email.',
                        })}
                    <StSuccessCard>
                      <FontAwesomeIcon size='lg' icon={['fal', 'message']} />
                      <div>
                        {t({
                          id: 'careProgramPaymentStatus.paymentSucceeded.card',
                          message: 'We nemen binnen 3 werkdagen contact met je op om een afspraak in te plannen.',
                        })}
                      </div>
                    </StSuccessCard>
                  </StStatusContainer>
                ),
              }}
              failedConfig={{
                title: t({
                  id: 'careProgramPaymentStatus.paymentFailed.title',
                  message: 'Betaling mislukt',
                }),
                body: (
                  <StStatusContainer>
                    <p>
                      {t({
                        id: 'careProgramPaymentStatus.paymentFailed.body',
                        message:
                          'Er ging iets mis bij het verwerken van je betaling. Controleer je betaalgegevens of probeer het opnieuw.',
                      })}
                    </p>

                    <p>
                      {t({
                        id: 'careProgramPaymentStatus.paymentFailed.contact',
                        message: 'Is het probleem aanhoudend? Neem contact op met de administratie.',
                      })}
                    </p>
                  </StStatusContainer>
                ),
              }}
            />
          )}
        </StContent>

        {hasError ? (
          <StFooter>
            <StButton variant='Primary' onClick={handleCancel}>
              {t({
                id: 'careProgramPaymentStatus.paymentFailed.cta',
                message: 'Probeer het opnieuw',
              })}
            </StButton>
          </StFooter>
        ) : cancelButtonVisible ? (
          <StFooter>
            <StButton variant='Primary' onClick={handleCancel}>
              {t({
                id: 'careProgramPaymentStatus.cancel',
                message: 'Betaling annuleren',
              })}
            </StButton>
          </StFooter>
        ) : (
          transactionStatus !== TransactionStatus.PROCESSING && (
            <StFooter>
              <StButton variant='Primary' onClick={handleCta}>
                {transactionStatus === TransactionStatus.COMPLETED
                  ? t({ id: 'globals.close', message: 'Sluiten' })
                  : t({
                      id: 'careProgramPaymentStatus.paymentFailed.cta',
                      message: 'Probeer het opnieuw',
                    })}
              </StButton>
            </StFooter>
          )
        )}
      </StContainer>
    </ContentPageLayout>
  )
}

const StAppHeader = styled(AppHeader)`
  ${({ theme }) => `
    padding: ${theme.UI.SpacingPx.Space3} ${theme.UI.SpacingPx.Space6};
    padding-top: 0;
  `}
`

const StContainer = styled.div`
  height: 100%;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  grid-template-areas:
    'content'
    'button';
`

const StContent = styled.div`
  ${ContentPagePaddingCSS}
  grid-area: content;

  margin: 0 auto;
  max-width: 600px;
  width: 100%;
`

const StErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  height: 100%;
`

const StStatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
`

const StSuccessCard = styled(Card)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};

  font-size: ${({ theme }) => theme.Typography.BodyMediumRegular['font-size']};
  font-weight: 500;
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};

  border: 1px solid ${({ theme }) => theme.Components.Card.Border};
  border-radius: 12px;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space4}
    ${({ theme }) => theme.UI.SpacingPx.Space6};
`

const StFooter = styled.div`
  grid-area: button;
  box-shadow: 0px -1px 20px rgba(4, 24, 49, 0.07);

  padding: ${({ theme }) => theme.UI.SpacingPx.Space3}
    ${({ theme }) => theme.UI.SpacingPx.Space6}
    calc(
      ${({ theme }) => theme.UI.SpacingPx.Space3} +
        var(--safe-area-inset-bottom)
    )
    ${({ theme }) => theme.UI.SpacingPx.Space6};
`

const StButton = styled(Button)`
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
`
